import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FIELD_CONTROL } from '@/components/ui/fieldClasses';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { resolveQuestionnaireDisplayName } from '@/lib/questionnaireDisplayName';
import type { FormsTemplateSummary } from '@/hooks/useFormsTemplates';
import {
  createQuestionnaireAutomationDraft,
  displayToMinutes,
  minutesToDisplay,
  parseOptionalTimingValue,
  sortAutomationsForDisplay,
  type QuestionnaireAutomationDraft,
  type QuestionnairePhase,
  type TimingUnit,
} from '@/lib/preVisitQuestionnaireTiming';
import { cn } from '@/utils/cn';
import { FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';

const RADIO_INPUT = 'h-4 w-4 border-ds-field text-ds-teal focus:shadow-ring';

function unitNoun(unit: TimingUnit, count: number, t: TFunction) {
  switch (unit) {
    case 'minutes':
      return t('services.timing_unit_minutes_noun', { count });
    case 'hours':
      return t('services.timing_unit_hours_noun', { count });
    case 'days':
      return t('services.timing_unit_days_noun', { count });
    default: {
      const _exhaustive: never = unit;
      return _exhaustive;
    }
  }
}

function sendSummary(row: QuestionnaireAutomationDraft, t: TFunction): string {
  if (row.offset_minutes === 0) {
    return row.phase === 'before_appointment'
      ? t('services.questionnaire_send_immediate_before')
      : t('services.questionnaire_send_immediate_after');
  }
  const display = minutesToDisplay(row.offset_minutes);
  const unit = unitNoun(display.unit, display.value, t);
  return row.phase === 'before_appointment'
    ? t('services.questionnaire_send_summary_before', { count: display.value, unit })
    : t('services.questionnaire_send_summary_after', { count: display.value, unit });
}

function templateName(
  templateId: string,
  templates: FormsTemplateSummary[],
  locale: 'da-DK' | 'en-DK',
  fallback: string,
): string {
  const tpl = templates.find((item) => item.id === templateId);
  return resolveQuestionnaireDisplayName({
    templateId,
    snapshotName: locale === 'en-DK' ? tpl?.display_name_en : tpl?.display_name_da,
    locale,
    templatesById: tpl
      ? {
          [tpl.id]: {
            id: tpl.id,
            display_name_da: tpl.display_name_da,
            display_name_en: tpl.display_name_en,
          },
        }
      : {},
    fallback,
  });
}

function TimingFields({
  row,
  onChange,
  phase,
}: {
  row: QuestionnaireAutomationDraft;
  onChange: (next: QuestionnaireAutomationDraft) => void;
  phase: QuestionnairePhase;
}) {
  const { t } = useTranslation();
  const offset = minutesToDisplay(row.offset_minutes);
  const [offsetUnit, setOffsetUnit] = useState<TimingUnit>(offset.unit);
  const [offsetDraft, setOffsetDraft] = useState<number | ''>(offset.value);

  const relationLabel =
    phase === 'before_appointment'
      ? t('services.questionnaire_before_appointment')
      : t('services.questionnaire_after_appointment');

  function commitOffset(raw: number | '', unit: TimingUnit) {
    const value = raw === '' ? 0 : raw;
    onChange({
      ...row,
      offset_minutes: displayToMinutes(value, unit),
      delay_after_booking_minutes: 0,
    });
  }

  return (
    <div>
      <label className={cn('block', FORM_LABEL_TYPE)}>
        {t('services.questionnaire_send_label')}
      </label>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          type="number"
          min={0}
          max={8760}
          value={offsetDraft}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => setOffsetDraft(parseOptionalTimingValue(e.target.value))}
          onBlur={() => {
            const next = offsetDraft === '' ? 0 : offsetDraft;
            setOffsetDraft(next);
            commitOffset(next, offsetUnit);
          }}
          className={cn(FIELD_CONTROL, 'w-24')}
          aria-label={t('services.questionnaire_send_label')}
        />
        <Select
          value={offsetUnit}
          aria-label={t('services.questionnaire_timing_unit')}
          onChange={(e) => {
            const next = e.target.value as TimingUnit;
            const displayed = offsetDraft === '' ? 0 : offsetDraft;
            setOffsetUnit(next);
            commitOffset(displayed, next);
          }}
          className="w-auto"
        >
          <option value="minutes">{t('services.timing_unit_minutes')}</option>
          <option value="hours">{t('services.timing_unit_hours')}</option>
          <option value="days">{t('services.timing_unit_days')}</option>
        </Select>
        <span className="text-sm text-ds-muted">{relationLabel}</span>
      </div>
    </div>
  );
}

function QuestionnairePicker({
  phase,
  templates,
  selectedIds,
  onAdd,
}: {
  phase: QuestionnairePhase;
  templates: FormsTemplateSummary[];
  selectedIds: string[];
  onAdd: (templateId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const locale = i18n.language.startsWith('en') ? 'en-DK' : 'da-DK';
  const available = templates.filter((tpl) => !selectedIds.includes(tpl.id));
  const filtered = available.filter((tpl) => {
    const name = templateName(tpl.id, templates, locale, tpl.id).toLowerCase();
    return name.includes(query.trim().toLowerCase());
  });

  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="listbox"
        data-testid={`questionnaire-add-${phase}`}
      >
        <Plus className="mr-1 h-4 w-4" />
        {t('services.questionnaires_add')}
      </Button>
      {open && (
        <div
          className="absolute z-20 mt-2 w-full min-w-[18rem] max-w-md rounded-lg border border-ds-line-strong bg-ds-card p-3 shadow-pop"
          role="listbox"
          aria-label={t('services.questionnaires_add')}
          data-testid={`questionnaire-picker-${phase}`}
        >
          <label className="sr-only" htmlFor={`questionnaire-search-${phase}`}>
            {t('services.questionnaires_search')}
          </label>
          <Input
            id={`questionnaire-search-${phase}`}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('services.questionnaires_search')}
            leadingIcon={<Search className="h-4 w-4" aria-hidden />}
          />
          <div className="mt-2 max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className={cn('px-1 py-2', FORM_HINT_TYPE)}>
                {t('services.questionnaires_picker_empty')}
              </p>
            ) : (
              filtered.map((tpl) => {
                const label = templateName(
                  tpl.id,
                  templates,
                  locale,
                  t('forms.unnamed_questionnaire', 'Spørgeskema'),
                );
                return (
                  <label
                    key={tpl.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm hover:bg-ds-row-hover"
                  >
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => {
                        onAdd(tpl.id);
                        setQuery('');
                        setOpen(false);
                      }}
                      className={RADIO_INPUT}
                    />
                    <span className="flex-1 text-ds-text">{label}</span>
                    {tpl.is_platform && (
                      <span className="rounded-full bg-ds-teal-tint px-1.5 py-0.5 text-xs font-medium text-ds-teal">
                        {t('services.template_platform_badge')}
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionnaireAutomationCard({
  row,
  templates,
  onChange,
  onRemove,
}: {
  row: QuestionnaireAutomationDraft;
  templates: FormsTemplateSummary[];
  onChange: (next: QuestionnaireAutomationDraft) => void;
  onRemove: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('en') ? 'en-DK' : 'da-DK';
  const name = templateName(
    row.forms_template_id,
    templates,
    locale,
    t('forms.unnamed_questionnaire', 'Spørgeskema'),
  );

  return (
    <div
      className="rounded-lg border border-ds-line px-4 py-3"
      data-testid="questionnaire-automation-card"
      data-phase={row.phase}
      data-template-id={row.forms_template_id}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={cn(TYPE.card, 'text-ds-text')}>{name}</p>
          <p className={cn('mt-0.5', FORM_HINT_TYPE)}>{sendSummary(row, t)}</p>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={onRemove}>
          {t('services.questionnaire_remove')}
        </Button>
      </div>
      <div className="mt-4 border-t border-ds-line pt-3">
        <TimingFields row={row} onChange={onChange} phase={row.phase} />
      </div>
    </div>
  );
}

export function QuestionnairePhaseList({
  phase,
  automations,
  templates,
  onChange,
  title: titleOverride,
}: {
  phase: QuestionnairePhase;
  automations: QuestionnaireAutomationDraft[];
  templates: FormsTemplateSummary[];
  onChange: (next: QuestionnaireAutomationDraft[]) => void;
  title?: string;
}) {
  const { t } = useTranslation();
  const rows = useMemo(
    () => sortAutomationsForDisplay(automations.filter((row) => row.phase === phase)),
    [automations, phase],
  );
  const selectedIds = rows.map((row) => row.forms_template_id);
  const title =
    titleOverride ??
    (phase === 'before_appointment'
      ? t('services.questionnaires_before')
      : t('services.questionnaires_after'));

  function addTemplate(templateId: string) {
    if (selectedIds.includes(templateId)) return;
    const draft = createQuestionnaireAutomationDraft({
      forms_template_id: templateId,
      phase,
      sort_order: rows.length,
    });
    onChange([...automations, draft]);
  }

  function updateRow(id: string, next: QuestionnaireAutomationDraft) {
    onChange(automations.map((row) => (row.id === id ? next : row)));
  }

  function removeRow(id: string) {
    onChange(automations.filter((row) => row.id !== id));
  }

  return (
    <div className="space-y-3" data-testid={`questionnaire-phase-${phase}`}>
      <h5 className={cn(TYPE.card, 'text-ds-text')}>{title}</h5>
      {rows.map((row) => (
        <QuestionnaireAutomationCard
          key={row.id}
          row={row}
          templates={templates}
          onChange={(next) => updateRow(row.id, next)}
          onRemove={() => removeRow(row.id)}
        />
      ))}
      <QuestionnairePicker
        phase={phase}
        templates={templates}
        selectedIds={selectedIds}
        onAdd={addTemplate}
      />
    </div>
  );
}
